import cx from "classnames";
import { type MutableRefObject, type RefObject } from "react";

import { Button, Flex, Loader } from "@theorgchartdev/storybook";
import type { FieldData as DocumentFieldData } from "../DocumentExport/DocumentExportContent";
import type { FieldData as ImageFieldData } from "../ImageExport/ImageExportContent";
import { GetWSMgr } from "@/Utils/StateLib";
import { PreviewPageControls } from "./PreviewPageControls";
import { type Scope, type ScopeController, usePreviewDocument } from "./usePreviewDocument";
import type { StatusController } from "@/hooks/useStatusController";
import { useStatusListener } from "@/hooks/useStatusListener";
import { QXDoc } from "@coreapp/CoreApp/QXDoc";
import { appstr } from "@coreapp/utils/appstr";

type Props = {
  fieldData: DocumentFieldData | ImageFieldData;
  backgroundRef: MutableRefObject<{ color: string; fillMode: number }>;
  mockDocumentRef: MutableRefObject<QXDoc | null>;
  sourceDocumentRef: MutableRefObject<QXDoc | null>;

  exportStartedRef: MutableRefObject<boolean>;
  statusController: StatusController;
  onPostBuild?: (mockDocument: QXDoc) => void;

  scopeControllerRef?: RefObject<ScopeController>;
  onReduceScope?: (scope: Scope) => void;
};

export const PreviewCanvas = (props: Props) => {
  const { canvasParentRef, setCanvasEl, rebuildPreviewDocument } = usePreviewDocument({
    fieldData: props.fieldData,
    exportStartedRef: props.exportStartedRef,
    backgroundRef: props.backgroundRef,
    mockDocumentRef: props.mockDocumentRef,
    scopeControllerRef: props.scopeControllerRef,
    onReduceScope: props.onReduceScope,
    sourceDocumentRef: props.sourceDocumentRef,
    statusController: props.statusController,
    onPostBuild: props.onPostBuild
  });

  useStatusListener({
    statusController: props.statusController,
    status: "error",
    listener: () => {
      const wsMgr = GetWSMgr();
      wsMgr?.CreateReactAlert(
        appstr("Unable to build preview"),
        appstr("An error prevented the preview from being generated. Please try again"),
        () => rebuildPreviewDocument(),
        null,
        appstr("Rebuild preview"),
        undefined,
        undefined,
        undefined,
        "preview-build-error"
      );
    }
  });

  return (
    <Flex
      width={"100%"}
      padding={"0 0 0 24px"}
      direction={"column"}
      justifyContent={"flex-start"}
      alignItems={"center"}
    >
      <Flex
        flex={1}
        className={cx("oc-border", "oc-border-t-0")}
        overflow={"hidden"}
        width={"100%"}
      >
        <Loader.Root>
          <Flex flex={1} ref={canvasParentRef}>
            <Flex position={"absolute"}>
              <canvas ref={setCanvasEl} />
            </Flex>
            {!props.statusController.isReady && (
              <Loader.Container dataTestId="preview-canvas-overlay">
                {props.statusController.isLoading && (
                  <>
                    <Loader.Spinner />
                    <Loader.Text>{appstr("createpreview")}</Loader.Text>
                  </>
                )}
                {props.statusController.hasError && (
                  <Button
                    iconProps={{ name: "arrows-rotate" }}
                    onClick={rebuildPreviewDocument}
                    modifier="secondary"
                  >
                    {appstr("Rebuild preview")}
                  </Button>
                )}
              </Loader.Container>
            )}
          </Flex>
        </Loader.Root>
      </Flex>
      <PreviewPageControls
        mockDocumentRef={props.mockDocumentRef}
        statusController={props.statusController}
      />
    </Flex>
  );
};
